import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import { AuthContext } from "../../context/Auth/AuthContext";
import { makeCtx } from "./engine";
import Demo from "./DemoKit";
import scenes from "./scenes";
import allSteps from "./steps";

const DONE_KEY = "flytecTutorialDone";

const useStyles = makeStyles((theme) => ({
	"@keyframes tourPop": {
		"0%": { opacity: 0, transform: "scale(0.92)" },
		"100%": { opacity: 1, transform: "scale(1)" },
	},
	"@keyframes tourRipple": {
		"0%": { transform: "translate(-50%, -50%) scale(.3)", opacity: 0.85 },
		"100%": { transform: "translate(-50%, -50%) scale(2.6)", opacity: 0 },
	},
	"@global": {
		"@keyframes flytecTourGlow": {
			"0%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.55)" },
			"70%": { boxShadow: "0 0 0 14px rgba(16, 185, 129, 0)" },
			"100%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" },
		},
		".flytec-tour-menu-glow": {
			position: "relative",
			zIndex: 2600,
			borderRadius: 10,
			animation: "flytecTourGlow 1.6s ease-out infinite",
			outline: "2px solid #10B981",
		},
	},
	blocker: {
		position: "fixed",
		inset: 0,
		zIndex: 2500,
	},
	"@keyframes tourFadeIn": {
		from: { opacity: 0 },
		to: { opacity: 1 },
	},
	"@keyframes tourBorderShift": {
		"0%": { backgroundPosition: "0 0, 0% 50%" },
		"50%": { backgroundPosition: "0 0, 100% 50%" },
		"100%": { backgroundPosition: "0 0, 0% 50%" },
	},
	"@keyframes tourRingGlow": {
		"0%, 100%": { boxShadow: "0 0 22px rgba(16,185,129,.4)" },
		"50%": { boxShadow: "0 0 52px rgba(16,185,129,.85)" },
	},
	// fundo DESFOCADO com recorte no que está em foco
	blurLayer: {
		position: "fixed",
		inset: 0,
		zIndex: 2505,
		background: "rgba(2, 6, 23, 0.5)",
		backdropFilter: "blur(7px) saturate(.85)",
		"-webkit-backdrop-filter": "blur(7px) saturate(.85)",
		pointerEvents: "none",
		animation: "$tourFadeIn .35s ease both",
	},
	"@keyframes tourRingShift": {
		"0%": { backgroundPosition: "0% 50%" },
		"50%": { backgroundPosition: "100% 50%" },
		"100%": { backgroundPosition: "0% 50%" },
	},
	// anel gradiente animado ao redor do foco (mask XOR: só a moldura pinta)
	ring: {
		position: "fixed",
		zIndex: 2506,
		borderRadius: 20,
		padding: 4,
		background:
			"linear-gradient(120deg, #10B981, #34D399, #3B82F6, #8B5CF6, #10B981)",
		backgroundSize: "300% 300%",
		"-webkit-mask":
			"linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
		"-webkit-mask-composite": "xor",
		mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
		maskComposite: "exclude",
		animation:
			"$tourRingShift 4s ease infinite, $tourRingGlow 2.6s ease infinite",
		transition:
			"top .5s cubic-bezier(.4,0,.2,1), left .5s cubic-bezier(.4,0,.2,1), width .5s cubic-bezier(.4,0,.2,1), height .5s cubic-bezier(.4,0,.2,1)",
		pointerEvents: "none",
	},
	anchorWrap: {
		position: "fixed",
		pointerEvents: "none",
		zIndex: 2520,
	},
	cursor: {
		position: "fixed",
		top: 0,
		left: 0,
		zIndex: 2560,
		pointerEvents: "none",
		filter: "drop-shadow(0 3px 6px rgba(2,6,23,.45))",
		transitionProperty: "transform",
		transitionTimingFunction: "cubic-bezier(.45,.05,.35,1)",
	},
	cursorInner: {
		transition: "transform .15s",
	},
	ripple: {
		position: "fixed",
		width: 44,
		height: 44,
		borderRadius: "50%",
		border: "3px solid #10B981",
		background: "rgba(16,185,129,.18)",
		pointerEvents: "none",
		zIndex: 2555,
		animation: "$tourRipple .7s ease-out forwards",
	},
	// barra de narração (rodapé)
	bar: {
		position: "fixed",
		bottom: 18,
		left: "50%",
		transform: "translateX(-50%)",
		zIndex: 2550,
		width: 560,
		maxWidth: "calc(100vw - 24px)",
		background: theme.palette.type === "dark" ? "#0F172A" : "#fff",
		color: theme.palette.text.primary,
		borderRadius: 16,
		border: "1px solid rgba(16, 185, 129, .35)",
		boxShadow: "0 18px 50px rgba(2, 6, 23, .45)",
		padding: "10px 14px 8px",
		animation: "$tourPop .35s cubic-bezier(.2,.9,.3,1.2)",
	},
	barTop: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	stepBadge: {
		fontSize: "0.62rem",
		fontWeight: 800,
		letterSpacing: "0.07em",
		color: "#fff",
		background: "linear-gradient(135deg, #10B981, #059669)",
		borderRadius: 999,
		padding: "3px 10px",
		whiteSpace: "nowrap",
	},
	barTitle: {
		fontWeight: 800,
		fontSize: "0.95rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		flex: 1,
	},
	barText: {
		fontSize: "0.76rem",
		color: theme.palette.text.secondary,
		margin: "4px 2px 6px",
		lineHeight: 1.45,
		display: "-webkit-box",
		"-webkit-line-clamp": 2,
		"-webkit-box-orient": "vertical",
		overflow: "hidden",
	},
	progressTrack: {
		height: 4,
		borderRadius: 2,
		background: "rgba(148,163,184,.25)",
		overflow: "hidden",
		marginBottom: 8,
	},
	progressFill: {
		height: "100%",
		background: "linear-gradient(90deg, #10B981, #34D399)",
		transition: "width .25s linear",
	},
	controls: {
		display: "flex",
		alignItems: "center",
		gap: 6,
	},
	dots: {
		display: "flex",
		gap: 4,
		margin: "0 auto",
	},
	dot: {
		width: 6,
		height: 6,
		borderRadius: "50%",
		background: "rgba(148,163,184,.4)",
		transition: "all .3s",
	},
	dotActive: {
		background: "#10B981",
		transform: "scale(1.4)",
	},
	nextBtn: {
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		fontWeight: 700,
		textTransform: "none",
		borderRadius: 10,
		padding: "4px 14px",
		"&:hover": { background: "linear-gradient(135deg, #059669, #047857)" },
	},
	ghostBtn: {
		textTransform: "none",
		fontWeight: 600,
		color: theme.palette.text.secondary,
		minWidth: 0,
	},
	centerCard: {
		position: "fixed",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		zIndex: 2550,
		width: 430,
		maxWidth: "calc(100vw - 32px)",
		background: theme.palette.type === "dark" ? "#0F172A" : "#fff",
		color: theme.palette.text.primary,
		borderRadius: 22,
		padding: 32,
		textAlign: "center",
		boxShadow: "0 24px 70px rgba(2, 6, 23, .55)",
		border: "1px solid rgba(16, 185, 129, .35)",
		animation: "$tourPop .4s cubic-bezier(.2,.9,.3,1.2)",
	},
	welcomeIcon: {
		width: 72,
		height: 72,
		margin: "0 auto 16px",
		borderRadius: 22,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		animation: "flytecTourGlow 2s ease-out infinite",
	},
	bigTitle: { fontWeight: 800, fontSize: "1.35rem", marginBottom: 8 },
	bigText: {
		fontSize: "0.9rem",
		color: theme.palette.text.secondary,
		lineHeight: 1.55,
		marginBottom: 18,
	},
	bigBtn: {
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		fontWeight: 800,
		textTransform: "none",
		borderRadius: 12,
		padding: "11px 0",
		fontSize: "0.95rem",
		width: "100%",
		"&:hover": { background: "linear-gradient(135deg, #059669, #047857)" },
	},
}));

// modais fictícios que exigem palco (fundo desfocado inteiro)
const STAGE_KINDS = ["qrCard", "formModal", "queueModal"];

// máscara SVG com furo arredondado no retângulo em foco
const maskFor = (r) => {
	const w = window.innerWidth;
	const h = window.innerHeight;
	const x = r.x - 10;
	const y = r.y - 10;
	const rw = r.w + 20;
	const rh = r.h + 20;
	const rad = 16;
	const d = `M0 0H${w}V${h}H0Z M${x + rad} ${y} h${rw - 2 * rad} a${rad} ${rad} 0 0 1 ${rad} ${rad} v${rh - 2 * rad} a${rad} ${rad} 0 0 1 -${rad} ${rad} h-${rw - 2 * rad} a${rad} ${rad} 0 0 1 -${rad} -${rad} v-${rh - 2 * rad} a${rad} ${rad} 0 0 1 ${rad} -${rad} Z`;
	const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><path d='${d}' fill='white' fill-rule='evenodd'/></svg>`;
	return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const CursorSvg = () => (
	<svg width="26" height="30" viewBox="0 0 26 30">
		<path
			d="M2 1 L2 24 L8.6 18.6 L12.6 27.4 L16.8 25.4 L12.8 16.9 L21.5 16.2 Z"
			fill="#fff"
			stroke="#0F172A"
			strokeWidth="1.7"
			strokeLinejoin="round"
		/>
	</svg>
);

// posiciona overlays fictícios sobre os containers reais
const AnchorLayer = ({ mounts }) => {
	const classes = useStyles();
	const [, force] = useState(0);
	useEffect(() => {
		const t = setInterval(() => force((v) => v + 1), 180);
		return () => clearInterval(t);
	}, []);
	return (
		<>
			{Object.values(mounts).map((m) => {
				const el = document.querySelector(m.anchor);
				if (!el) return null;
				const r = el.getBoundingClientRect();
				return (
					<div
						key={m.id}
						className={classes.anchorWrap}
						style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
					>
						<div style={{ position: "relative", width: "100%", height: "100%" }}>
							<Demo kind={m.kind} props={m.props} />
						</div>
					</div>
				);
			})}
		</>
	);
};

const Tutorial = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const [phase, setPhase] = useState("closed"); // closed | welcome | running | finale
	const [stepIndex, setStepIndex] = useState(0);
	const [cursor, setCursor] = useState({
		x: -100,
		y: -100,
		duration: 800,
		visible: false,
		pressing: false,
	});
	const [ripples, setRipples] = useState([]);
	const [mounts, setMounts] = useState({});
	const [spotRect, setSpotRect] = useState(null);
	const [muted, setMuted] = useState(false);
	const [progress, setProgress] = useState(0);

	const audioRef = useRef(null);
	const glowRef = useRef(null);
	const rippleSeq = useRef(0);

	const isAdmin = user?.profile === "admin";
	const steps = allSteps.filter((s) => !s.adminOnly || isAdmin);
	const step = steps[stepIndex];

	const addRipple = useCallback((p) => {
		rippleSeq.current += 1;
		const id = rippleSeq.current;
		setRipples((rs) => [...rs, { id, x: p.x, y: p.y }]);
		setTimeout(
			() => setRipples((rs) => rs.filter((r) => r.id !== id)),
			800
		);
	}, []);

	// abertura automática no primeiro login + evento dos botões "Tutorial"
	useEffect(() => {
		const start = () => {
			setStepIndex(0);
			setPhase("welcome");
		};
		window.addEventListener("flytec:start-tutorial", start);
		let timer;
		if (user?.id && !localStorage.getItem(DONE_KEY)) {
			timer = setTimeout(start, 1200);
		}
		return () => {
			window.removeEventListener("flytec:start-tutorial", start);
			if (timer) clearTimeout(timer);
		};
	}, [user?.id]);

	const clearGlow = () => {
		if (glowRef.current) {
			glowRef.current.classList.remove("flytec-tour-menu-glow");
			glowRef.current = null;
		}
	};

	// ciclo de vida de cada passo: navega, roda a CENA e o ÁUDIO em paralelo;
	// avança quando os dois terminam (ou no Próximo manual)
	useEffect(() => {
		if (phase !== "running" || !step) return undefined;

		const ac = new AbortController();
		const ctx = makeCtx({
			signal: ac.signal,
			history,
			setCursor,
			addRipple,
			setMounts,
			setSpotRect,
		});

		let audioDone = false;
		let sceneDone = false;
		let advanceTimer = null;
		let hardTimer = null;
		const stepStart = Date.now();
		const isLast = stepIndex >= steps.length - 1;

		// avança apenas uma vez, e nunca antes de um tempo mínimo de passo —
		// mesmo que áudio/cena terminem cedo por qualquer motivo, o tutorial
		// não pode "metralhar" passos
		const maybeAdvance = () => {
			if (!audioDone || !sceneDone || advanceTimer) return;
			const elapsed = Date.now() - stepStart;
			const delay = Math.max(800, 9000 - elapsed);
			advanceTimer = setTimeout(() => {
				if (isLast) {
					localStorage.setItem(DONE_KEY, "1");
					setPhase("finale");
				} else {
					setStepIndex(stepIndex + 1);
				}
			}, delay);
		};

		// áudio
		const audio = audioRef.current;
		setProgress(0);
		let onTime = null;
		let onEnded = null;
		if (audio) {
			audio.src = step.audio;
			audio.muted = muted;
			onTime = () => {
				if (audio.duration)
					setProgress((audio.currentTime / audio.duration) * 100);
			};
			onEnded = () => {
				// ignora "ended" que não seja do áudio deste passo (troca de src
				// em andamento) ou términos instantâneos suspeitos
				if (!audio.src || audio.src.indexOf(step.audio) === -1) return;
				if (audio.currentTime < 2) return;
				setProgress(100);
				audioDone = true;
				maybeAdvance();
			};
			audio.addEventListener("timeupdate", onTime);
			audio.addEventListener("ended", onEnded);
			const p = audio.play();
			if (p && p.catch)
				p.catch(() => {
					// autoplay bloqueado: cena comanda o ritmo
					audioDone = true;
					maybeAdvance();
				});
		} else {
			audioDone = true;
		}

		// glow no item do menu
		clearGlow();
		if (step.menu) {
			const el = document.querySelector(step.menu);
			if (el) {
				el.classList.add("flytec-tour-menu-glow");
				glowRef.current = el;
			}
		}

		// cena
		(async () => {
			try {
				await ctx.navigate(step.path);
				await ctx.waitFor("[data-tour='main-content']", 5000);
				// glow pode ter chegado depois da navegação
				if (step.menu && !glowRef.current) {
					const el = document.querySelector(step.menu);
					if (el) {
						el.classList.add("flytec-tour-menu-glow");
						glowRef.current = el;
					}
				}
				const scene = scenes[step.id];
				if (scene) await scene(ctx);
			} catch (e) {
				// SceneCancelled (navegação manual) ou erro de cena: nunca travar
				if (e && e.name !== "SceneCancelled")
					// eslint-disable-next-line no-console
					console.warn("[tutorial] cena falhou:", step.id, e);
			}
			sceneDone = true;
			maybeAdvance();
		})();

		// trava de segurança: nenhum passo dura mais de 50s
		hardTimer = setTimeout(() => {
			audioDone = true;
			sceneDone = true;
			maybeAdvance();
		}, 50000);

		return () => {
			ac.abort();
			if (advanceTimer) clearTimeout(advanceTimer);
			if (hardTimer) clearTimeout(hardTimer);
			if (audio) {
				if (onTime) audio.removeEventListener("timeupdate", onTime);
				if (onEnded) audio.removeEventListener("ended", onEnded);
				audio.pause();
			}
			setMounts({});
			setSpotRect(null);
			setCursor((c) => ({ ...c, visible: false }));
			clearGlow();
		};
	}, [phase, stepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (audioRef.current) audioRef.current.muted = muted;
	}, [muted]);

	const close = (done = true) => {
		if (done) localStorage.setItem(DONE_KEY, "1");
		clearGlow();
		if (audioRef.current) audioRef.current.pause();
		setPhase("closed");
	};

	const startTour = () => {
		setStepIndex(0);
		setPhase("running");
		// gesto do usuário desbloqueia o autoplay da narração
		if (audioRef.current) {
			const p = audioRef.current.play();
			if (p && p.catch) p.catch(() => {});
		}
	};

	const next = () => {
		if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
		else {
			localStorage.setItem(DONE_KEY, "1");
			setPhase("finale");
		}
	};
	const prev = () => {
		if (stepIndex > 0) setStepIndex(stepIndex - 1);
	};

	if (phase === "closed") {
		return <audio ref={audioRef} style={{ display: "none" }} />;
	}

	return (
		<>
			<audio ref={audioRef} style={{ display: "none" }} />
			<div className={classes.blocker} />

			{phase === "welcome" && (
				<div className={classes.centerCard}>
					<div className={classes.welcomeIcon}>
						<WhatsAppIcon style={{ fontSize: 38 }} />
					</div>
					<Typography className={classes.bigTitle}>
						Bem-vindo ao Flytec! 🎉
					</Typography>
					<Typography className={classes.bigText}>
						Preparei uma <b>demonstração guiada por voz</b>: eu mesmo vou
						navegar pelas telas, clicar nos botões e mostrar tudo funcionando
						com dados de exemplo. São só 3 minutinhos! 🎧
					</Typography>
					<Button className={classes.bigBtn} onClick={startTour}>
						🎧 Começar a demonstração
					</Button>
					<Button
						className={classes.ghostBtn}
						onClick={() => close(true)}
						style={{ marginTop: 8 }}
					>
						Agora não
					</Button>
				</div>
			)}

			{phase === "finale" && (
				<>
					<Demo kind="confetti" props={{}} />
					<div className={classes.centerCard}>
						<div className={classes.welcomeIcon}>
							<CheckCircleIcon style={{ fontSize: 38 }} />
						</div>
						<Typography className={classes.bigTitle}>
							Prontinho! Você já conhece o Flytec 🚀
						</Typography>
						<Typography className={classes.bigText}>
							Agora é a sua vez: conecte o seu WhatsApp e comece a atender.
							Se quiser rever, o tutorial está sempre no menu à esquerda.
						</Typography>
						<Button
							className={classes.bigBtn}
							onClick={() => {
								close(true);
								history.push("/connections");
							}}
						>
							<WhatsAppIcon style={{ fontSize: 20, marginRight: 8 }} />
							Conectar meu WhatsApp
						</Button>
						<Button
							className={classes.ghostBtn}
							onClick={() => close(true)}
							style={{ marginTop: 8 }}
						>
							Concluir
						</Button>
					</div>
				</>
			)}

			{phase === "running" && step && (
				<>
					{(spotRect ||
						Object.values(mounts).some((m) =>
							STAGE_KINDS.includes(m.kind)
						)) && (
						<div
							key={spotRect ? `${spotRect.x}-${spotRect.y}` : "stage"}
							className={classes.blurLayer}
							style={
								spotRect
									? {
											WebkitMaskImage: maskFor(spotRect),
											maskImage: maskFor(spotRect),
									  }
									: {}
							}
						/>
					)}
					{spotRect && (
						<div
							className={classes.ring}
							style={{
								top: spotRect.y - 13,
								left: spotRect.x - 13,
								width: spotRect.w + 26,
								height: spotRect.h + 26,
							}}
						/>
					)}

					<AnchorLayer mounts={mounts} />

					{ripples.map((r) => (
						<span
							key={r.id}
							className={classes.ripple}
							style={{ top: r.y, left: r.x, transform: "translate(-50%,-50%)" }}
						/>
					))}

					<div
						className={classes.cursor}
						style={{
							transform: `translate(${cursor.x}px, ${cursor.y}px)`,
							transitionDuration: `${cursor.duration}ms`,
							opacity: cursor.visible ? 1 : 0,
						}}
					>
						<div
							className={classes.cursorInner}
							style={{
								transform: cursor.pressing ? "scale(.8)" : "scale(1)",
							}}
						>
							<CursorSvg />
						</div>
					</div>

					<div className={classes.bar}>
						<div className={classes.barTop}>
							<span className={classes.stepBadge}>
								{stepIndex + 1}/{steps.length}
							</span>
							<Typography className={classes.barTitle}>
								{step.title}
							</Typography>
							<div className={classes.controls}>
								<IconButton size="small" onClick={() => setMuted(!muted)}>
									{muted ? (
										<VolumeOffIcon fontSize="small" />
									) : (
										<VolumeUpIcon
											fontSize="small"
											style={{ color: "#10B981" }}
										/>
									)}
								</IconButton>
								<Button
									size="small"
									className={classes.ghostBtn}
									onClick={prev}
									disabled={stepIndex === 0}
								>
									<ArrowBackIcon fontSize="small" />
								</Button>
								<Button size="small" className={classes.nextBtn} onClick={next}>
									{stepIndex === steps.length - 1 ? (
										<>
											Concluir <CheckCircleIcon style={{ fontSize: 16, marginLeft: 6 }} />
										</>
									) : (
										<>
											Próximo <ArrowForwardIcon style={{ fontSize: 16, marginLeft: 6 }} />
										</>
									)}
								</Button>
								<IconButton size="small" onClick={() => close(true)}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</div>
						</div>
						<Typography className={classes.barText}>{step.text}</Typography>
						<div className={classes.progressTrack}>
							<div
								className={classes.progressFill}
								style={{ width: `${progress}%` }}
							/>
						</div>
						<div className={classes.dots}>
							{steps.map((s, i) => (
								<span
									key={s.id}
									className={`${classes.dot} ${
										i === stepIndex ? classes.dotActive : ""
									}`}
								/>
							))}
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default Tutorial;
